import {
  type Ticket,
  type TicketCategory,
  type TicketDepartment,
  type TicketEvent,
  TicketEventKind,
  type TicketFieldValue,
  type TicketFormField,
  TicketFormFieldType,
  TicketSla,
  TicketStatus,
  type TicketSubject,
  type TicketSubjectPath,
} from './_models';
import { findSubjectPath } from './_utils';

type SubjectRow = [id: string, name: string, hint: string, sla: TicketSla];
type CategoryRow = [name: string, subjects: SubjectRow[]];
type EventDraft = Omit<TicketEvent, 'id' | 'attachments'> & { attachments?: string[] };

const H24 = TicketSla.HOURS_24;
const H48 = TicketSla.HOURS_48;
const H8 = TicketSla.HOURS_8;

export const MOCK_STORES = ['Loja Renascença', 'Loja Cohama'];

export const MOCK_PRODUCTS = [
  'Café Torrado e Moído 500g · 7896005800010',
  'Iogurte Natural 170g · 7891000055519',
  'Leite UHT Integral 1L · 7891000100103',
  'Arroz Branco Tipo 1 5kg · 7896006700015',
];

export const MOCK_INVOICES = [
  'NF-e 18.233 · Distribuidora Bom Sabor Ltda.',
  'NF-e 18.310 · Frios Maranhão S.A.',
  'NF-e 4.512 · Bebidas Ilha Comércio Ltda.',
];

export const MOCK_TERMINALS = ['PDV 3 · Caixa 3', 'PDV 1 · Caixa 1', 'PDV 2 · Caixa 2'];

function requiredField(
  label: string,
  type: TicketFormFieldType,
  showInTitle: boolean,
  options?: string[],
): TicketFormField {
  return { label, type, required: true, showInTitle, options };
}

const PRODUCT_FIELD = requiredField('Produto', TicketFormFieldType.OPTIONS, true, MOCK_PRODUCTS);
const TERMINAL_FIELD = requiredField('Terminal', TicketFormFieldType.OPTIONS, true, MOCK_TERMINALS);
const INVOICE_FORM = [requiredField('Nota fiscal', TicketFormFieldType.OPTIONS, true, MOCK_INVOICES)];
const EXPIRY_FORM = [
  PRODUCT_FIELD,
  requiredField('Quantidade', TicketFormFieldType.QUANTITY, false),
  requiredField('Validade', TicketFormFieldType.DATE, true),
];

const FORM_BY_SUBJECT_ID: Record<string, TicketFormField[]> = {
  'divergencia-precos': [
    PRODUCT_FIELD,
    requiredField('Preço na etiqueta', TicketFormFieldType.MONEY, true),
    requiredField('Preço no PDV', TicketFormFieldType.MONEY, true),
    {
      label: 'Foto da etiqueta',
      type: TicketFormFieldType.UPLOAD,
      required: false,
      showInTitle: false,
      help: 'Uma foto ajuda o Pricing a conferir mais rápido.',
    },
  ],
  'rebaixa-validade': EXPIRY_FORM,
  'rebaixa-critica': EXPIRY_FORM,
  'liberacao-notas': INVOICE_FORM,
  'nota-com-divergencia': INVOICE_FORM,
  'erro-carga-precos-pdv': [TERMINAL_FIELD],
  pdv: [TERMINAL_FIELD],
  impressora: [TERMINAL_FIELD],
  balanca: [TERMINAL_FIELD],
  'sem-conexao-servidor': [
    TERMINAL_FIELD,
    requiredField('O provedor de internet está funcionando?', TicketFormFieldType.YES_NO, false),
    requiredField('Os outros caixas estão conectados?', TicketFormFieldType.YES_NO, false),
    requiredField('Você já reiniciou o PDV?', TicketFormFieldType.YES_NO, false),
  ],
};

const INSTRUCTION_BY_SUBJECT_ID: Record<string, string> = {
  'sem-conexao-servidor': 'confira se o cabo de rede está conectado e se o modem está ligado.',
};

const CATALOG_ROWS: [department: string, categories: CategoryRow[]][] = [
  [
    'Pricing',
    [
      [
        'Preço de venda',
        [
          ['divergencia-precos', 'Divergência de preços', 'Etiqueta diferente do caixa', H24],
          ['erro-carga-precos-pdv', 'Erro na carga de preços do PDV', 'Caixa com preço desatualizado', H24],
          ['alteracao-de-preco-pontual', 'Alteração de preço pontual', 'Pedido do gerente', H48],
        ],
      ],
      [
        'Rebaixa',
        [
          ['rebaixa-validade', 'Rebaixa por validade', 'Produto perto do vencimento', H24],
          ['rebaixa-critica', 'Rebaixa crítica', 'Vencimento em até 2 dias', H24],
        ],
      ],
      [
        'Cadastro de preço',
        [
          ['produtos-via-cd', 'Produtos via CD', 'Preço de item recebido do CD', H48],
          ['produtos-fundo-de-loja', 'Produtos fundo de loja', 'Preço de item comprado fora do CD', H48],
        ],
      ],
    ],
  ],
  [
    'Inteligência Comercial',
    [
      [
        'Mix por seção',
        [
          ['mercearia-bomboniere-e-check-stand', 'Mercearia, bomboniere e check-stand', 'Mix e cadastro da seção', H48],
          ['bebidas-frios-e-congelados', 'Bebidas, frios e congelados', 'Mix e cadastro da seção', H48],
          ['higiene-limpeza-bazar-e-pet', 'Higiene, limpeza, bazar e pet', 'Mix e cadastro da seção', H48],
        ],
      ],
    ],
  ],
  [
    'Validação de notas fiscais',
    [
      [
        'Fundo de loja',
        [
          ['liberacao-notas', 'Liberação de notas', 'Nota de fornecedor do fundo de loja', H24],
          ['nota-com-divergencia', 'Nota com divergência', 'Itens ou valores diferentes do pedido', H24],
        ],
      ],
    ],
  ],
  [
    'Operações',
    [
      [
        'Materiais',
        [['pedido-de-almoxarifado', 'Pedido de almoxarifado', 'Sacolas, bobinas, material de limpeza', H48]],
      ],
      ['Inventário', [['agendamento-de-inventario', 'Agendamento de inventário', 'Data e equipe do inventário', H48]]],
      ['Equipe', [['treinamento', 'Treinamento', 'Treinar equipe nova', H48]]],
      ['Geral', [['assuntos-gerais', 'Assuntos gerais', 'Qualquer outro assunto com a matriz', H48]]],
    ],
  ],
  [
    'Marketing',
    [
      [
        'Materiais de venda',
        [
          ['encarte', 'Encarte', 'Erro ou falta no encarte da semana', H48],
          ['acao-promocional', 'Ação promocional', 'Pedido de ação na loja', H48],
        ],
      ],
    ],
  ],
  ['Financeiro', [['Geral', [['financeiro-geral', 'Financeiro geral', 'Boletos, repasses e cobranças', H48]]]]],
  [
    'Suporte T.I.',
    [
      [
        'Equipamentos',
        [
          ['pdv', 'PDV', 'Caixa travado ou fora do ar', H24],
          ['impressora', 'Impressora', 'Cupom ou etiqueta não imprime', H24],
          ['balanca', 'Balança', 'Peso ou etiqueta da balança', H24],
        ],
      ],
      ['PDV', [['sem-conexao-servidor', 'Sem conexão com o servidor', 'Caixa sem comunicação com o servidor', H8]]],
    ],
  ],
];

function buildSubject([id, name, hint, sla]: SubjectRow): TicketSubject {
  return {
    id,
    name,
    hint,
    sla,
    form: FORM_BY_SUBJECT_ID[id] ?? [],
    instruction: INSTRUCTION_BY_SUBJECT_ID[id],
  };
}

function buildCategory(departmentName: string, [name, subjectRows]: CategoryRow): TicketCategory {
  return { id: `${departmentName}/${name}`, name, subjects: subjectRows.map(buildSubject) };
}

export const MOCK_TICKET_CATALOG: TicketDepartment[] = CATALOG_ROWS.map(([name, categoryRows]) => ({
  id: name,
  name,
  categories: categoryRows.map((categoryRow) => buildCategory(name, categoryRow)),
}));

const PRICE_FIELDS: TicketFieldValue[] = [
  { label: 'Produto', value: 'Café Torrado e Moído 500g · 7896005800010' },
  { label: 'Preço na etiqueta', value: 'R$ 17,99' },
  { label: 'Preço no PDV', value: 'R$ 18,49' },
];

const EXPIRY_FIELDS: TicketFieldValue[] = [
  { label: 'Produto', value: 'Iogurte Natural 170g · 7891000055519' },
  { label: 'Quantidade', value: '12 un.' },
  { label: 'Validade', value: '03/11/2026' },
];

const INVOICE_FIELDS: TicketFieldValue[] = [
  { label: 'Nota fiscal', value: 'NF-e 18.233 · Distribuidora Bom Sabor Ltda.' },
];

const TERMINAL_FIELDS: TicketFieldValue[] = [{ label: 'Terminal', value: 'PDV 3 · Caixa 3' }];

export const MOCK_DEFAULT_REQUESTER = 'Marina Costa';
const STORE = 'Loja Renascença';
const SECOND_STORE = 'Loja Cohama';

function subjectPath(subjectId: string): TicketSubjectPath {
  const found = findSubjectPath(MOCK_TICKET_CATALOG, subjectId);
  if (!found) throw new Error(`Assunto do mock não encontrado: ${subjectId}`);
  return { subjectId, department: found.department, category: found.category, subject: found.subject };
}

function withEventIds(ticketNumber: number, drafts: EventDraft[]): TicketEvent[] {
  return drafts.map((draft, index) => ({
    ...draft,
    id: `${ticketNumber}-${index + 1}`,
    attachments: draft.attachments ?? [],
  }));
}

function openedDraft(at: string, text: string, attachments: string[] = []): EventDraft {
  return {
    kind: TicketEventKind.OPENED,
    author: MOCK_DEFAULT_REQUESTER,
    role: 'você',
    mine: true,
    at,
    text,
    attachments,
  };
}

function autoReplyDraft(at: string, department: string, dueLabel: string): EventDraft {
  const text = `Recebemos seu pedido. A equipe de ${department} confere e responde até ${dueLabel}.`;
  return { kind: TicketEventKind.AUTO_REPLY, author: 'Resposta automática', role: 'sistema', mine: false, at, text };
}

function assignedDraft(at: string, author: string, role: string): EventDraft {
  const text = `${author} (${role}) assumiu o chamado`;
  return { kind: TicketEventKind.ASSIGNED, author, role, mine: false, at, text };
}

function messageDraft(at: string, author: string, role: string, text: string): EventDraft {
  return { kind: TicketEventKind.MESSAGE, author, role, mine: false, at, text };
}

function statusChangedDraft(at: string, status: TicketStatus, text: string): EventDraft {
  return { kind: TicketEventKind.STATUS_CHANGED, author: 'Sistema', role: 'sistema', mine: false, at, text, status };
}

function resolvedDraft(at: string, author: string, role: string, department: string): EventDraft {
  const text = `${department} resolveu o chamado`;
  return { kind: TicketEventKind.RESOLVED, author, role, mine: false, at, text };
}

export function createMockTickets(): Ticket[] {
  const pricingDepartment = 'Pricing';
  const supportDepartment = 'Suporte T.I.';
  const marketingDepartment = 'Marketing';
  const operationsDepartment = 'Operações';
  const rafael = 'Rafael Lima';

  const ticket1039Drafts: EventDraft[] = [
    openedDraft('ontem, 18:02', 'Iogurte natural com validade próxima. Preciso de rebaixa.'),
    autoReplyDraft('ontem, 18:02', pricingDepartment, 'hoje, 18:02'),
    assignedDraft('hoje, 09:40', rafael, pricingDepartment),
    messageDraft(
      'há 1 hora',
      rafael,
      pricingDepartment,
      'Oi, Marina! Pode enviar uma foto do lote e da validade? Com ela eu libero a rebaixa.',
    ),
    statusChangedDraft('há 1 hora', TicketStatus.WAITING_STORE, 'Pricing pediu uma informação'),
  ];

  const ticket1039: Ticket = {
    id: '1039',
    number: 1039,
    title: 'Rebaixa por validade: iogurte natural 170g (12 un.)',
    status: TicketStatus.WAITING_STORE,
    path: subjectPath('rebaixa-validade'),
    sla: TicketSla.HOURS_24,
    dueLabel: 'Hoje, 18:02',
    dueAt: '2026-10-30T18:02:00',
    late: false,
    store: STORE,
    requester: MOCK_DEFAULT_REQUESTER,
    assignee: rafael,
    openedAt: 'ontem, 18:02',
    origin: 'Chamados',
    fields: EXPIRY_FIELDS,
    events: withEventIds(1039, ticket1039Drafts),
    lastUpdate: 'Pricing pediu a foto do lote',
    lastUpdateAt: 'há 1 hora',
  };

  const ticket1042Drafts: EventDraft[] = [
    openedDraft(
      'hoje, 10:12',
      'A etiqueta da gôndola mostra um preço e o caixa cobra outro. Cliente reclamou no caixa 1.',
      ['foto-etiqueta.jpg'],
    ),
    autoReplyDraft('hoje, 10:12', pricingDepartment, 'segunda, 02/11, 10:12'),
    assignedDraft('hoje, 10:40', rafael, pricingDepartment),
    messageDraft(
      'hoje, 11:05',
      rafael,
      pricingDepartment,
      'Oi, Marina! A tabela da rede está com R$ 18,49 desde segunda. A etiqueta da gôndola ficou com o preço antigo. Pode reimprimir em Cadastros › Etiquetas? Se o encarte da semana mostrar R$ 17,99, me avise que eu ajusto.',
    ),
    {
      kind: TicketEventKind.MESSAGE,
      author: MOCK_DEFAULT_REQUESTER,
      role: 'você',
      mine: true,
      at: 'hoje, 11:32',
      text: 'O encarte mostra R$ 17,99, sim. Segue a foto.',
      attachments: ['encarte-semana-44.jpg'],
    },
    messageDraft(
      'há 20 min',
      rafael,
      pricingDepartment,
      'Obrigado! Vou alinhar com Marketing e te retorno ainda hoje.',
    ),
  ];

  const ticket1042: Ticket = {
    id: '1042',
    number: 1042,
    title: 'Café torrado 500g com preço diferente no PDV',
    status: TicketStatus.IN_PROGRESS,
    path: subjectPath('divergencia-precos'),
    sla: TicketSla.HOURS_24,
    dueLabel: '02/11, 10:12',
    dueAt: '2026-11-02T10:12:00',
    late: false,
    store: STORE,
    requester: MOCK_DEFAULT_REQUESTER,
    assignee: rafael,
    openedAt: 'hoje, 10:12',
    origin: 'Cadastros › Produtos › Café Torrado e Moído 500g',
    fields: PRICE_FIELDS,
    events: withEventIds(1042, ticket1042Drafts),
    lastUpdate: 'Rafael (Pricing) respondeu',
    lastUpdateAt: 'há 20 min',
  };

  const ticket1036Drafts: EventDraft[] = [
    openedDraft('28/10, 16:40', 'Nota parada na entrada da loja. Preciso da liberação para receber a mercadoria.'),
    autoReplyDraft('28/10, 16:40', 'Validação de notas fiscais', '29/10, 16:40'),
  ];

  const ticket1036: Ticket = {
    id: '1036',
    number: 1036,
    title: 'Liberar a NF 18.233 da Distribuidora Bom Sabor',
    status: TicketStatus.NEW,
    path: subjectPath('liberacao-notas'),
    sla: TicketSla.HOURS_24,
    dueLabel: 'Atrasado há 2 h',
    dueAt: '2026-10-29T16:40:00',
    late: true,
    store: STORE,
    requester: MOCK_DEFAULT_REQUESTER,
    openedAt: '28/10, 16:40',
    origin: 'Chamados',
    fields: INVOICE_FIELDS,
    events: withEventIds(1036, ticket1036Drafts),
    lastUpdate: 'Aberto por você',
    lastUpdateAt: '28/10, 16:40',
  };

  const ticket1034Drafts: EventDraft[] = [
    openedDraft('ontem, 11:05', 'Faltam sacolas e bobinas de cupom para os caixas.'),
    autoReplyDraft('ontem, 11:05', operationsDepartment, 'segunda, 02/11, 11:05'),
    assignedDraft('ontem, 11:05', 'Operações', 'equipe'),
  ];

  const ticket1034: Ticket = {
    id: '1034',
    number: 1034,
    title: 'Sacolas e bobinas para os caixas',
    status: TicketStatus.IN_PROGRESS,
    path: subjectPath('pedido-de-almoxarifado'),
    sla: TicketSla.HOURS_48,
    dueLabel: '02/11, 11:05',
    dueAt: '2026-11-02T11:05:00',
    late: false,
    store: SECOND_STORE,
    requester: MOCK_DEFAULT_REQUESTER,
    assignee: 'Equipe de Operações',
    openedAt: 'ontem, 11:05',
    origin: 'Chamados',
    fields: [],
    events: withEventIds(1034, ticket1034Drafts),
    lastUpdate: 'Operações assumiu o chamado',
    lastUpdateAt: 'ontem, 11:05',
  };

  const ticket1031Drafts: EventDraft[] = [
    openedDraft(
      'ontem, 17:48',
      'O PDV 3 está cobrando os preços da semana passada. Os outros caixas estão certos. Exemplo: Leite UHT 1L sai R$ 5,79 no PDV 3 e R$ 5,49 nos outros.',
    ),
    autoReplyDraft('ontem, 17:48', pricingDepartment, 'hoje, 17:48'),
    assignedDraft('hoje, 08:30', rafael, pricingDepartment),
    messageDraft(
      'hoje, 09:12',
      rafael,
      pricingDepartment,
      'Reenviei a carga de preços para o PDV 3. O terminal recebeu às 09:10 e o Leite UHT 1L já sai R$ 5,49.',
    ),
    resolvedDraft('hoje, 09:12', rafael, pricingDepartment, pricingDepartment),
  ];

  const ticket1031: Ticket = {
    id: '1031',
    number: 1031,
    title: 'Erro na carga de preços do PDV 3',
    status: TicketStatus.RESOLVED,
    path: subjectPath('erro-carga-precos-pdv'),
    sla: TicketSla.HOURS_24,
    dueLabel: 'No prazo',
    dueAt: '2026-10-30T17:48:00',
    late: false,
    resolvedOnTime: true,
    resolutionTimeLabel: '2 h 24 min úteis',
    store: STORE,
    requester: MOCK_DEFAULT_REQUESTER,
    assignee: rafael,
    openedAt: 'ontem, 17:48',
    origin: 'Chamados',
    fields: TERMINAL_FIELDS,
    events: withEventIds(1031, ticket1031Drafts),
    lastUpdate: 'Pricing resolveu o chamado',
    lastUpdateAt: 'hoje, 09:12',
  };

  const ticket1027Drafts: EventDraft[] = [
    openedDraft('27/10, 10:20', 'A impressora do caixa 2 liga, mas não imprime o cupom.'),
    autoReplyDraft('27/10, 10:20', supportDepartment, '28/10, 10:20'),
    assignedDraft('27/10, 11:00', 'Suporte T.I.', 'equipe'),
    resolvedDraft('27/10, 15:30', 'Suporte T.I.', 'equipe', supportDepartment),
  ];

  const ticket1027: Ticket = {
    id: '1027',
    number: 1027,
    title: 'Impressora do caixa 2 não imprime o cupom',
    status: TicketStatus.RESOLVED,
    path: subjectPath('impressora'),
    sla: TicketSla.HOURS_24,
    dueLabel: 'No prazo',
    dueAt: '2026-10-28T10:20:00',
    late: false,
    resolvedOnTime: true,
    resolutionTimeLabel: '4 h 30 min úteis',
    store: STORE,
    requester: MOCK_DEFAULT_REQUESTER,
    assignee: 'Equipe de Suporte T.I.',
    openedAt: '27/10, 10:20',
    origin: 'Chamados',
    fields: [{ label: 'Terminal', value: 'PDV 2 · Caixa 2' }],
    events: withEventIds(1027, ticket1027Drafts),
    lastUpdate: 'Suporte T.I. resolveu o chamado',
    lastUpdateAt: '27/10, 15:30',
  };

  const ticket1019Drafts: EventDraft[] = [
    openedDraft('19/10, 09:30', 'O encarte da semana saiu sem o preço do arroz.'),
    autoReplyDraft('19/10, 09:30', marketingDepartment, '21/10, 09:30'),
    assignedDraft('21/10, 14:00', 'Marketing', 'equipe'),
    resolvedDraft('22/10, 10:02', 'Marketing', 'equipe', marketingDepartment),
  ];

  const ticket1019: Ticket = {
    id: '1019',
    number: 1019,
    title: 'Encarte da semana sem o preço do arroz',
    status: TicketStatus.RESOLVED,
    path: subjectPath('encarte'),
    sla: TicketSla.HOURS_48,
    dueLabel: 'Fora do prazo',
    dueAt: '2026-10-21T09:30:00',
    late: false,
    resolvedOnTime: false,
    resolutionTimeLabel: '1 dia e 6 h úteis',
    store: SECOND_STORE,
    requester: MOCK_DEFAULT_REQUESTER,
    assignee: 'Equipe de Marketing',
    openedAt: '19/10, 09:30',
    origin: 'Chamados',
    fields: [],
    events: withEventIds(1019, ticket1019Drafts),
    lastUpdate: 'Marketing resolveu o chamado',
    lastUpdateAt: '22/10, 10:02',
  };

  return [ticket1039, ticket1042, ticket1036, ticket1034, ticket1031, ticket1027, ticket1019];
}
